/**
 * Build Recharts datasets from MediTrack report rows (each row may include `metrics` from PDF extraction).
 */

function getNumericMetric(metrics, key) {
  if (!metrics || !metrics[key]) return null;
  const v = metrics[key].value;
  if (typeof v === "number" && !Number.isNaN(v)) return v;
  const n = parseFloat(v);
  return Number.isNaN(n) ? null : n;
}

function normalizeWbc(val) {
  if (val == null) return null;
  if (val > 50) return val / 1000;
  return val;
}

function clamp(n) {
  return Math.max(0, Math.min(100, Math.round(n)));
}

function avgParts(parts) {
  const ok = parts.filter((p) => p != null && !Number.isNaN(p));
  if (!ok.length) return null;
  return ok.reduce((a, b) => a + b, 0) / ok.length;
}

export function formatShortPeriod(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "2-digit" });
}

/** Stable unique X-axis key per report so Recharts tooltips work when two uploads share the same calendar label. */
export function reportTickKey(row, indexInSeries) {
  if (row?.id != null) {
    const id = row.id;
    const s = typeof id === "string" ? id : typeof id.toString === "function" ? id.toString() : String(id);
    if (s && s !== "[object Object]") return s;
  }
  const t = row?.rawDate != null ? new Date(row.rawDate).getTime() : 0;
  return `x-${t}-${indexInSeries}`;
}

/** Human-readable tick / tooltip label from chart row (month, period, or tickKey fallback). */
export function formatChartTickLabel(data, tickKey) {
  if (tickKey == null) return "";
  const pt = Array.isArray(data) ? data.find((d) => d.tickKey === tickKey) : null;
  if (!pt) return String(tickKey);
  const calendarLabel =
    pt.month != null && pt.month !== "" ? pt.month : pt.period != null && pt.period !== "" ? pt.period : null;
  const base = calendarLabel ?? String(tickKey);
  if (calendarLabel && Array.isArray(data)) {
    const sameDay = data.filter((d) => {
      const l = d.month != null && d.month !== "" ? d.month : d.period != null && d.period !== "" ? d.period : null;
      return l === calendarLabel;
    });
    if (sameDay.length > 1) {
      const ord = sameDay.findIndex((d) => d.tickKey === tickKey) + 1;
      return `${base} (${ord})`;
    }
  }
  return base;
}

/** Ensure every row has tickKey (demo data and legacy rows). */
export function withChartTickKeys(rows, prefix = "pt") {
  if (!Array.isArray(rows)) return [];
  return rows.map((r, i) => ({
    ...r,
    tickKey: r.tickKey != null && r.tickKey !== "" ? r.tickKey : `${prefix}-${i}`
  }));
}

/** Oldest → newest */
function sortRowsChronological(rows) {
  return [...rows].sort((a, b) => {
    const ta = a.rawDate ? new Date(a.rawDate).getTime() : 0;
    const tb = b.rawDate ? new Date(b.rawDate).getTime() : 0;
    return ta - tb;
  });
}

/** Newest first */
function sortRowsNewestFirst(rows) {
  return [...rows].sort((a, b) => {
    const ta = a.rawDate ? new Date(a.rawDate).getTime() : 0;
    const tb = b.rawDate ? new Date(b.rawDate).getTime() : 0;
    return tb - ta;
  });
}

export function buildCbcLineData(rows) {
  const sorted = sortRowsChronological(rows);
  const out = [];
  for (const row of sorted) {
    const m = row.metrics;
    if (!m || !Object.keys(m).length) continue;
    const wbc = normalizeWbc(getNumericMetric(m, "wbc"));
    const rbc = getNumericMetric(m, "rbc");
    const hgb = getNumericMetric(m, "hemoglobin") ?? getNumericMetric(m, "hgb");
    if (wbc == null && rbc == null && hgb == null) continue;
    out.push({
      tickKey: reportTickKey(row, out.length),
      month: formatShortPeriod(row.rawDate),
      wbc: wbc ?? null,
      rbc: rbc ?? null,
      hgb: hgb ?? null
    });
  }
  return out;
}

export function buildLipidBarData(rows) {
  const sorted = sortRowsChronological(rows);
  const out = [];
  for (const row of sorted) {
    const m = row.metrics;
    if (!m) continue;
    const ldl = getNumericMetric(m, "ldl");
    const hdl = getNumericMetric(m, "hdl");
    const trig = getNumericMetric(m, "triglycerides");
    if (ldl == null && hdl == null && trig == null) continue;
    out.push({
      tickKey: reportTickKey(row, out.length),
      period: formatShortPeriod(row.rawDate),
      ldl: ldl ?? null,
      hdl: hdl ?? null,
      trig: trig ?? null
    });
  }
  return out;
}

function snapshotScores(metrics) {
  if (!metrics || !Object.keys(metrics).length) {
    return { blood: 70, heart: 70, kidney: 70, liver: 70, thyroid: 70, nutrition: 70 };
  }

  const wbc = normalizeWbc(getNumericMetric(metrics, "wbc"));
  const rbc = getNumericMetric(metrics, "rbc");
  const hgb = getNumericMetric(metrics, "hemoglobin");
  const bloodParts = [];
  if (wbc != null) bloodParts.push(clamp(100 - Math.abs(wbc - 7) * 8));
  if (rbc != null) bloodParts.push(clamp(100 - Math.abs(rbc - 5) * 14));
  if (hgb != null) bloodParts.push(clamp(100 - Math.abs(hgb - 14) * 6));

  const ldl = getNumericMetric(metrics, "ldl");
  const hdl = getNumericMetric(metrics, "hdl");
  const trig = getNumericMetric(metrics, "triglycerides");
  const heartParts = [];
  if (ldl != null) heartParts.push(ldl < 100 ? 95 : ldl < 130 ? 82 : ldl < 160 ? 68 : 48);
  if (hdl != null) heartParts.push(hdl >= 60 ? 95 : hdl >= 40 ? 78 : 55);
  if (trig != null) heartParts.push(trig < 150 ? 90 : trig < 200 ? 70 : 50);

  const cr = getNumericMetric(metrics, "creatinine");
  const bun = getNumericMetric(metrics, "bun");
  const kidneyParts = [];
  if (cr != null) kidneyParts.push(cr < 1.1 ? 92 : cr < 1.3 ? 75 : 55);
  if (bun != null) kidneyParts.push(bun < 20 ? 90 : bun < 40 ? 72 : 50);

  const alt = getNumericMetric(metrics, "sgpt");
  const ast = getNumericMetric(metrics, "sgot");
  const liverParts = [];
  if (alt != null) liverParts.push(alt < 40 ? 92 : alt < 80 ? 70 : 48);
  if (ast != null) liverParts.push(ast < 40 ? 92 : ast < 80 ? 70 : 48);

  const tsh = getNumericMetric(metrics, "tsh");
  let thyroidScore = 70;
  if (tsh != null) {
    thyroidScore = tsh >= 0.5 && tsh <= 4.5 ? 92 : clamp(88 - Math.abs(tsh - 2.5) * 10);
  }

  const vitD = getNumericMetric(metrics, "vitaminD");
  const b12 = getNumericMetric(metrics, "vitaminB12");
  const nutParts = [];
  if (vitD != null) nutParts.push(vitD >= 30 && vitD <= 60 ? 95 : vitD >= 20 ? 72 : 48);
  if (b12 != null) nutParts.push(b12 >= 300 ? 90 : b12 >= 200 ? 75 : 50);

  return {
    blood: Math.round(avgParts(bloodParts) ?? 70),
    heart: Math.round(avgParts(heartParts) ?? 70),
    kidney: Math.round(avgParts(kidneyParts) ?? 70),
    liver: Math.round(avgParts(liverParts) ?? 70),
    thyroid: Math.round(thyroidScore),
    nutrition: Math.round(avgParts(nutParts) ?? 70)
  };
}

/** Recharts shape: { domain, current, previous }[] */
export function buildRadarFromReports(rows) {
  const withData = sortRowsNewestFirst(rows).filter((r) => r.metrics && Object.keys(r.metrics).length);
  if (!withData.length) return [];

  const latest = snapshotScores(withData[0].metrics);
  const prevMetrics = withData[1]?.metrics;
  const previous = prevMetrics ? snapshotScores(prevMetrics) : latest;

  const keys = [
    { domain: "Blood", k: "blood" },
    { domain: "Heart", k: "heart" },
    { domain: "Kidney", k: "kidney" },
    { domain: "Liver", k: "liver" },
    { domain: "Thyroid", k: "thyroid" },
    { domain: "Nutrition", k: "nutrition" }
  ];

  return keys.map(({ domain, k }) => ({
    domain,
    current: latest[k],
    previous: previous[k]
  }));
}

export function buildGlucoseAreaData(rows) {
  const sorted = sortRowsChronological(rows);
  const out = [];
  for (const row of sorted) {
    const m = row.metrics;
    if (!m) continue;
    const glucose =
      getNumericMetric(m, "bloodGlucoseFasting") ??
      getNumericMetric(m, "bloodGlucoseRandom") ??
      getNumericMetric(m, "bloodGlucose");
    const hba1c = getNumericMetric(m, "hba1c");
    if (glucose == null && hba1c == null) continue;
    out.push({
      tickKey: reportTickKey(row, out.length),
      month: formatShortPeriod(row.rawDate),
      glucose: glucose ?? null,
      hba1c: hba1c ?? null
    });
  }
  return out;
}

export function buildTshLineData(rows) {
  const sorted = sortRowsChronological(rows);
  const out = [];
  for (const row of sorted) {
    const m = row.metrics;
    if (!m) continue;
    const tsh = getNumericMetric(m, "tsh");
    if (tsh == null) continue;
    out.push({ tickKey: reportTickKey(row, out.length), month: formatShortPeriod(row.rawDate), tsh });
  }
  return out;
}

export function buildVitaminBarData(rows) {
  const sorted = sortRowsChronological(rows);
  const out = [];
  for (const row of sorted) {
    const m = row.metrics;
    if (!m) continue;
    const vitD = getNumericMetric(m, "vitaminD");
    const b12 = getNumericMetric(m, "vitaminB12");
    if (vitD == null && b12 == null) continue;
    out.push({
      tickKey: reportTickKey(row, out.length),
      month: formatShortPeriod(row.rawDate),
      vitD: vitD ?? null,
      b12: b12 ?? null
    });
  }
  return out;
}

export function buildBmiAreaData(rows) {
  const sorted = sortRowsChronological(rows);
  const out = [];
  for (const row of sorted) {
    const m = row.metrics;
    if (!m) continue;
    const bmi = getNumericMetric(m, "bmi");
    if (bmi == null) continue;
    out.push({ tickKey: reportTickKey(row, out.length), month: formatShortPeriod(row.rawDate), bmi });
  }
  return out;
}

/** Metric keys already shown in the four primary MediTrack trend charts. */
const PRIMARY_TREND_METRIC_KEYS = new Set([
  "bloodGlucoseFasting",
  "bloodGlucoseRandom",
  "bloodGlucose",
  "hba1c",
  "tsh",
  "vitaminD",
  "vitaminB12",
  "bmi"
]);

function numericFromMeta(meta) {
  if (!meta || meta.value === undefined || meta.value === null) return null;
  if (typeof meta.value === "number" && !Number.isNaN(meta.value)) return meta.value;
  const n = parseFloat(meta.value);
  return Number.isNaN(n) ? null : n;
}

/**
 * One line series per extra metric (CBC, lipids, liver enzymes, dynamic PDF keys, etc.).
 * @returns {Array<{ key: string, label: string, unit: string, data: Array<{ month: string, value: number }> }>}
 */
export function buildAdditionalMetricTrends(rows) {
  if (!Array.isArray(rows) || !rows.length) return [];
  const sorted = sortRowsChronological(rows);
  const keys = new Set();
  for (const row of sorted) {
    const m = row.metrics;
    if (!m) continue;
    for (const k of Object.keys(m)) {
      if (!PRIMARY_TREND_METRIC_KEYS.has(k)) keys.add(k);
    }
  }

  const series = [];
  for (const key of keys) {
    let label = key;
    let unit = "";
    const data = [];
    for (const row of sorted) {
      const m = row.metrics;
      if (!m || !m[key]) continue;
      const meta = m[key];
      const v = numericFromMeta(meta);
      if (v == null) continue;
      if (typeof meta.label === "string" && meta.label.trim()) label = meta.label.trim();
      if (typeof meta.unit === "string" && meta.unit.trim()) unit = meta.unit.trim();
      data.push({
        tickKey: reportTickKey(row, data.length),
        month: formatShortPeriod(row.rawDate),
        value: v
      });
    }
    if (!data.length) continue;
    series.push({ key, label, unit, data });
  }

  series.sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: "base" }));
  return series;
}

export function rowsHaveAnyMetrics(rows) {
  return Array.isArray(rows) && rows.some((r) => r.metrics && Object.keys(r.metrics).length > 0);
}
